import React from 'react';
import CodeBlock from '../components/CodeBlock';

const Templates = () => {
  return (
    <div className="page">
      <div className="page-header">
        <h1>📋 Templates</h1>
        <p>
          Lister les templates WhatsApp de votre workspace et créer / soumettre de nouveaux
          templates texte à l'approbation Meta directement via l'API.
        </p>
      </div>

      <div className="section">
        <h2>Liste des templates</h2>
        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-get">GET</span>
            <span className="endpoint-path">/api/v1/templates</span>
          </div>

          <h4>Query parameters</h4>
          <table className="params-table">
            <thead><tr><th>Param</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>status</code></td><td>DRAFT, PUBLISHED</td></tr>
              <tr><td><code>statusMeta</code></td><td>APPROVED, PENDING, REJECTED</td></tr>
              <tr><td><code>language</code></td><td>fr, en, es…</td></tr>
              <tr><td><code>category</code></td><td>MARKETING, UTILITY, AUTHENTICATION</td></tr>
              <tr><td><code>limit</code> / <code>offset</code></td><td>Pagination</td></tr>
            </tbody>
          </table>

          <CodeBlock language="bash" code={`curl "https://api.whakup.com/api/v1/templates?statusMeta=APPROVED" \\
  -H "Authorization: Bearer YOUR_JWT" \\
  -H "X-Workspace-Id: 42"`} />

          <h4>Réponse 200</h4>
          <CodeBlock language="json" code={`{
  "data": [
    {
      "id": 12,
      "name": "promo_ete",
      "language": "fr",
      "category": "MARKETING",
      "status": "PUBLISHED",
      "statusMeta": "APPROVED",
      "metaTemplateId": "1234567890"
    }
  ],
  "pagination": { "limit": 50, "offset": 0 }
}`} />
        </div>
      </div>

      <div className="section">
        <h2>Détail d'un template</h2>
        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-get">GET</span>
            <span className="endpoint-path">/api/v1/templates/{'{id}'}</span>
          </div>
          <p>Retourne le template complet avec tous les composants (header, body, footer, boutons).</p>
        </div>
      </div>

      <div className="section">
        <h2>Créer / soumettre un template</h2>
        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-post">POST</span>
            <span className="endpoint-path">/api/v1/templates</span>
          </div>
          <div className="endpoint-description">
            Crée n'importe quel type de template — classique (texte) ou carrousel — et le soumet
            à Meta pour approbation. Requiert le scope <code>templates:write</code>.
          </div>

          <div className="alert alert-info" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            ⚠️ Nécessite authentification JWT + header <code>X-Workspace-Id</code> + scope <code>templates:write</code>
          </div>

          <h4>Champs du body</h4>
          <table className="params-table">
            <thead><tr><th>Champ</th><th>Type</th><th>Requis</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>name</code></td><td>string</td><td>✅</td><td>Nom du template (minuscules, chiffres, underscores)</td></tr>
              <tr><td><code>language</code></td><td>string</td><td>✅</td><td>Code langue Meta (ex. <code>fr</code>, <code>en</code>)</td></tr>
              <tr><td><code>body</code></td><td>string</td><td>✅*</td><td>Corps du message. Variables nommées <code>{'{prenom}'}</code> (max 4). *Requis sauf carrousel</td></tr>
              <tr><td><code>category</code></td><td>string</td><td>❌</td><td><code>MARKETING</code> (défaut), <code>UTILITY</code> ou <code>AUTHENTICATION</code></td></tr>
              <tr><td><code>header</code></td><td>string | object</td><td>❌</td><td>En-tête : chaîne (texte) ou objet (voir En-têtes)</td></tr>
              <tr><td><code>footer</code></td><td>string</td><td>❌</td><td>Pied de message</td></tr>
              <tr><td><code>buttons</code></td><td>array</td><td>❌</td><td>Boutons (voir Boutons). Pour un carrousel : partagés par toutes les cartes (1–2)</td></tr>
              <tr><td><code>cards</code></td><td>array</td><td>❌</td><td>Sa présence crée un <strong>carrousel</strong> (2 à 10 cartes — voir Carrousel)</td></tr>
              <tr><td><code>submit</code></td><td>bool</td><td>❌</td><td><code>true</code> (défaut) = soumis à Meta ; <code>false</code> = brouillon</td></tr>
            </tbody>
          </table>

          <h4>En-têtes (<code>header</code>)</h4>
          <p>Une chaîne est un raccourci pour un en-tête texte. Sinon, un objet :</p>
          <ul>
            <li><code>{'{ "type": "text", "text": "Titre" }'}</code> — en-tête texte (sans emoji)</li>
            <li><code>{'{ "type": "image", "media": "data:image/png;base64,..." }'}</code> — en-tête image</li>
            <li><code>{'{ "type": "video", "media": "data:video/mp4;base64,..." }'}</code> — en-tête vidéo (MP4 uniquement)</li>
          </ul>

          <h4>Boutons (<code>buttons</code>)</h4>
          <table className="params-table">
            <thead><tr><th>type</th><th>Champs</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>url</code></td><td><code>text</code>, <code>url</code></td><td>Bouton lien (CTA)</td></tr>
              <tr><td><code>phone</code></td><td><code>text</code>, <code>phoneNumber</code></td><td>Bouton appel (CTA)</td></tr>
              <tr><td><code>quick_reply</code></td><td><code>text</code></td><td>Réponse rapide</td></tr>
              <tr><td><code>opt_out</code></td><td>—</td><td>Bouton de désinscription (« Arrêter les promotions »)</td></tr>
            </tbody>
          </table>

          <h4>Carrousel (<code>cards</code>)</h4>
          <p>
            Si <code>cards</code> est présent, le template est un carrousel : <strong>2 à 10 cartes</strong>,
            chacune avec une <strong>image</strong> obligatoire (<code>media</code> base64), un <code>body</code> optionnel,
            et des boutons. Les boutons (1–2) sont définis une fois dans <code>buttons</code> et partagés par toutes les cartes.
          </p>

          <h4>Exemple — template classique (en-tête image + boutons)</h4>
          <CodeBlock language="json" code={`{
  "name": "promo_ete",
  "language": "fr",
  "category": "MARKETING",
  "header": { "type": "image", "media": "data:image/png;base64,iVBORw0KGgo..." },
  "body": "Bonjour {prenom}, profitez de -20% jusqu'à dimanche !",
  "footer": "Répondre STOP pour vous désinscrire",
  "buttons": [
    { "type": "url", "text": "Voir l'offre", "url": "https://exemple.com/promo" },
    { "type": "phone", "text": "Nous appeler", "phoneNumber": "+33123456789" },
    { "type": "opt_out" }
  ],
  "submit": true
}`} />

          <h4>Exemple — carrousel</h4>
          <CodeBlock language="json" code={`{
  "name": "catalogue_ete",
  "language": "fr",
  "body": "Découvrez notre sélection {prenom} :",
  "buttons": [
    { "type": "url", "text": "Acheter", "url": "https://exemple.com/shop" }
  ],
  "cards": [
    { "media": "data:image/png;base64,iVBORw0...", "body": "Modèle A — 49€" },
    { "media": "data:image/png;base64,iVBORw0...", "body": "Modèle B — 59€" }
  ],
  "submit": true
}`} />

          <h4>Exemple — minimal (cURL)</h4>
          <CodeBlock language="bash" code={`curl -X POST https://api.whakup.com/api/v1/templates \\
  -H "Authorization: Bearer YOUR_JWT" \\
  -H "X-Workspace-Id: 42" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"promo_ete","language":"fr","body":"Bonjour {prenom} !"}'`} />

          <h4>Réponse 201</h4>
          <CodeBlock language="json" code={`{
  "data": {
    "id": 87,
    "name": "promo_ete",
    "language": "fr",
    "category": "MARKETING",
    "status": "PUBLISHED",
    "statusMeta": "PENDING",
    "metaTemplateId": "1234567890"
  }
}`} />
          <p>
            Le template part en validation Meta : <code>statusMeta</code> vaut généralement
            <code>PENDING</code> juste après la soumission, puis passe à <code>APPROVED</code> ou
            <code>REJECTED</code>. Abonnez-vous aux webhooks <code>template.approved</code> /
            <code>template.rejected</code> pour être notifié.
          </p>

          <h4>Erreurs possibles</h4>
          <ul>
            <li><strong>400</strong> — champ requis manquant, &gt; 4 variables, URL/bouton invalide, carrousel hors 2–10 cartes, ou erreur de validation Meta (<code>origin: "meta"</code>)</li>
            <li><strong>409</strong> — un template publié avec ce nom et cette langue existe déjà</li>
          </ul>

          <div className="alert alert-info" style={{ marginTop: '1rem' }}>
            💡 Les médias (en-tête image/vidéo, images de carrousel) sont transmis en <strong>base64 (data URI)</strong>,
            ex. <code>data:image/png;base64,...</code>. Les vidéos doivent être au format <strong>MP4</strong>.
          </div>
        </div>
      </div>

      <div className="section">
        <h2>Scopes requis</h2>
        <ul>
          <li><code>templates:read</code> — lecture (<code>GET</code>)</li>
          <li><code>templates:write</code> — création (<code>POST</code>)</li>
        </ul>
      </div>
    </div>
  );
};

export default Templates;
